import { z } from "zod";
import type { ESignature } from "./charts";

/**
 * Forms, charts, and notes from the old system (Boulevard), read in the browser from the export
 * files and sent in batches. Kept exactly as signed there, flagged with where they came from.
 */
export const IMPORTED_KINDS = ["intake", "consent", "instructions", "chart", "form", "file"] as const;
export type ImportedKind = (typeof IMPORTED_KINDS)[number];

/** One line of an imported form: a heading, some wording, a question and its answer, a signature, or a file. */
export const importedItemSchema = z.object({
  kind: z.enum(["heading", "text", "answer", "signature", "file"]),
  label: z.string().max(5000).default(""),
  value: z.string().max(50_000).default(""),
  /** Uploading: a data URL (image or file). Stored: the file's storage key. Viewing: a link. */
  file: z.string().max(8_000_000).nullable().default(null),
});
export type ImportedItem = z.infer<typeof importedItemSchema>;

const iso = z.string().max(40).nullable().default(null);

export const bvRecordSchema = z.object({
  ref: z.string().min(1).max(100),
  kind: z.enum(IMPORTED_KINDS),
  name: z.string().max(300),
  status: z.string().max(50).default(""),
  signedAt: iso,
  signedBy: z.string().max(300).default(""),
  visitRef: z.string().max(100).nullable().default(null),
  voidedAt: iso,
  sourceReview: z.object({ at: iso, by: z.string().max(300).default(""), note: z.string().max(5000).default("") }).nullable().default(null),
  needsReview: z.boolean().default(false),
  content: z.array(importedItemSchema).max(500),
});
export type BvRecord = z.infer<typeof bvRecordSchema>;

export const bvNoteSchema = z.object({
  ref: z.string().min(1).max(100),
  kind: z.enum(["allergy", "medication"]).nullable().default(null),
  text: z.string().max(20_000),
  at: z.string().max(40),
  author: z.string().max(300).default(""),
});

export const bvClientSchema = z.object({
  ref: z.string().min(1).max(100),
  name: z.string().max(300).default(""),
  email: z.string().max(320).nullable().default(null),
  records: z.array(bvRecordSchema).max(1000),
  notes: z.array(bvNoteSchema).max(5000),
});
export type BvClient = z.infer<typeof bvClientSchema>;

export interface BoulevardImportResult {
  records: number;
  notes: number;
  files: number;
  unmatched: number;
}

/** An imported form or chart in a client's list (with the clinic's own). */
export interface ImportedRecordSummary {
  id: string;
  source: string;
  kind: ImportedKind;
  name: string;
  status: string;
  signedAt: string | null;
  signedBy: string;
  visitRef: string | null;
  voided: boolean;
  /** The medical director: reviewed in the old system, here, or still waiting (charts). */
  review: { status: "not_needed" } | { status: "waiting" } | { status: "reviewed"; by: string; at: string | null; note: string; where: "source" | "here" };
}

export interface ImportedRecordView extends ImportedRecordSummary {
  clientId: string;
  clientName: string;
  content: ImportedItem[];
  /** The medical director's electronic signature on a review done here. */
  reviewSignature: ESignature | null;
}

export const SOURCE_LABELS: Record<string, string> = { boulevard: "Boulevard" };

// ── Live, read-only pull from Boulevard (staging first)

/** Boulevard's own pages call one "graph" address; Vaulted only ever sends it to Boulevard. */
const boulevardEndpoint = z
  .string()
  .trim()
  .url("Paste the full address, starting with https://")
  .refine((v) => {
    try {
      const u = new URL(v);
      return u.protocol === "https:" && (u.hostname === "boulevard.io" || u.hostname.endsWith(".boulevard.io"));
    } catch {
      return false;
    }
  }, "That isn't a Boulevard address (it should be https://…boulevard.io/…)");

/**
 * Connecting: the "Copy as cURL" text of one of Boulevard's "graph" requests, pasted into Vaulted.
 * Only the address, the cookies, the CSRF token, and the business ID are kept (encrypted).
 */
export const boulevardConnectionSchema = z.object({
  // The page drops the request's body first; cookies alone can run to a few thousand characters.
  curl: z.string().trim().min(20, "Paste the copied request").max(200_000, "That's too long to be one copied request"),
});
export type BoulevardConnectionInput = z.infer<typeof boulevardConnectionSchema>;

/** Where Boulevard's own pages send their requests (used when signing in with a login). */
export const BOULEVARD_ENDPOINT = "https://dashboard.boulevard.io/api/v1.0/graph";

/** Signing in with a Boulevard login (best: one made just for Vaulted). Kept encrypted, never shown back. */
export const boulevardLoginSchema = z.object({
  email: z.string().trim().toLowerCase().max(254).email("Check the email address"),
  password: z.string().min(1, "Enter the password").max(200),
});
export type BoulevardLoginInput = z.infer<typeof boulevardLoginSchema>;

/**
 * The parts of a pasted "Copy as cURL" Vaulted keeps. The CSRF token falls back to the csrf-token
 * cookie (the same value); the business ID can be looked up after connecting, so it may be null.
 */
export function parseBoulevardCurl(curl: string): { endpoint: string; cookie: string; csrf: string; businessId: string | null } | { error: string } {
  // Chrome (Mac/Linux) quotes with '…' or $'…'; Windows "cmd" copies use "…"; lines end in " \" or " ^".
  const text = curl.replace(/[\\^]\r?\n/g, " ").replace(/\r?\n/g, " ");
  const arg = (flag: string) => {
    const m = text.match(new RegExp(`(?:^|\\s)(?:${flag})\\s+(?:\\$?'([^']*)'|"((?:\\\\.|[^"\\\\])*)")`, "i"));
    return m ? (m[1] ?? m[2] ?? "").replace(/\\(.)/g, "$1") : null;
  };
  const headers = [...text.matchAll(/(?:^|\s)(?:-H|--header)\s+(?:\$?'([^']*)'|"((?:\\.|[^"\\])*)")/gi)].map((m) => (m[1] ?? m[2] ?? "").replace(/\\(.)/g, "$1"));
  const header = (name: string) => headers.find((h) => h.toLowerCase().startsWith(`${name}:`))?.slice(name.length + 1).trim() || null;
  const url = arg("--url") ?? text.match(/curl\s+(?:\$?'([^']+)'|"([^"]+)")/)?.slice(1).find(Boolean) ?? null;
  if (!url || !boulevardEndpoint.safeParse(url).success) return { error: "That isn't a request to Boulevard. Copy one of the rows named \"graph\"." };
  const cookie = arg("-b|--cookie") ?? header("cookie");
  if (!cookie) return { error: "The cookies are missing from this copy. In Chrome, use Copy → Copy as cURL (not Copy as fetch) on a row named \"graph\"." };
  const csrf = header("x-csrf-token") ?? cookie.match(/(?:^|;\s*)csrf-token=([^;]+)/)?.[1] ?? null;
  if (!csrf) return { error: "This copy has no CSRF token (neither the x-csrf-token header nor the csrf-token cookie). Copy a different row named \"graph\", after clicking around in Boulevard." };
  return { endpoint: url, cookie, csrf, businessId: header("x-blvd-bid") };
}

export interface BoulevardConnectionView {
  /** Off in production until Ryan says so. */
  allowed: boolean;
  connected: boolean;
  endpoint: string | null;
  mode: "session" | "login" | null;
  lastCheckedAt: string | null;
  lastOkAt: string | null;
  lastOkAs: string | null;
  lastError: string | null;
  /** The saved login (never its password); stopped says why Vaulted has stopped signing in with it. */
  login: { email: string; stopped: string | null } | null;
  /** Flags from the appointment sync waiting for someone. */
  openFlags: number;
  /** Receipts: whether Boulevard said how to find an appointment's (null: not asked yet), how many are saved, and imported past visits still without one. */
  receipts: { linkFound: boolean | null; saved: number; pastVisitsWithout: number };
  /**
   * Taking over Boulevard's visits at cutover: when it was done (null: not yet), how many upcoming
   * synced visits there are to take over, and when appointments were last synced.
   */
  takeover: { at: string | null; upcoming: number; lastSyncAt: string | null };
  /** Latest sync runs, newest first. */
  runs: { id: string; kind: "clients" | "appointments" | "receipts" | "records"; status: "running" | "done" | "failed"; counts: Record<string, number>; progress: string; error: string | null; startedAt: string; finishedAt: string | null;
    /** Clients it couldn't read: who they are here (if anyone), Boulevard's ID, and what Boulevard said. */
    failed: { boulevardId: string; clientId: string | null; name: string | null; error: string }[];
  }[];
}

/** A visit's checkout as Boulevard recorded it (cents). */
export interface BoulevardReceiptView {
  orderNumber: string | null;
  status: string;
  closedAt: string | null;
  voided: boolean;
  /** This visit's lines (the whole order's when it was the only visit on it). */
  lines: {
    kind: "service" | "product" | "tip" | "gift_card" | "account_credit" | "other";
    name: string;
    quantity: number;
    beforeCents: number;
    cents: number;
    discounts: { name: string; cents: number }[];
    addOns: string[];
    refundedCents: number;
  }[];
  payments: { kind: "card" | "voucher" | "gift_card" | "product_card" | "cash" | "other"; label: string; cents: number; card: string | null; refundedCents: number }[];
  tipCents: number;
  taxCents: number;
  feeCents: number;
  /** The whole order's total (what was paid, before refunds). */
  totalCents: number;
  refundedCents: number;
  /** One checkout for several people's visits: lines are this visit's; payments and total are everyone's. */
  shared: boolean;
  /** This visit's lines other than the tip, after discounts. */
  servicesCents: number;
}

// ── Appointments (staging): staff, services, and locations matched first, then visits

export type BoulevardMapKind = "staff" | "service" | "location";

export interface BoulevardMappingRow {
  boulevardId: string;
  /** Its name in Boulevard. */
  label: string;
  vaultedId: string | null;
  skipped: boolean;
}

export interface BoulevardMappingView {
  staff: BoulevardMappingRow[];
  services: BoulevardMappingRow[];
  locations: BoulevardMappingRow[];
  /** What each can be matched to here. */
  options: { staff: { id: string; name: string }[]; services: { id: string; name: string; type: "service" | "add_on"; category: string | null }[]; locations: { id: string; name: string }[] };
  /** When Boulevard's lists were last read. */
  readAt: string | null;
}

export const boulevardMapSchema = z.object({
  kind: z.enum(["staff", "service", "location"]),
  boulevardId: z.string().min(1).max(200),
  vaultedId: z.string().uuid().nullable(),
  skipped: z.boolean().default(false),
});

/** Sync appointments from this date (clinic-local) through 90 days ahead. */
export const boulevardAppointmentSyncSchema = z.object({ from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a date") });
export const BOULEVARD_DAYS_AHEAD = 90;

export const BOULEVARD_FLAG_KINDS = ["differs_from_import", "possible_duplicate", "overlap", "changed_after_start", "cancelled_after_start", "gone", "no_chair", "no_card"] as const;
export type BoulevardFlagKind = (typeof BOULEVARD_FLAG_KINDS)[number];

export const BOULEVARD_FLAG_LABELS: Record<BoulevardFlagKind, string> = {
  differs_from_import: "Differs from the imported Boulevard record",
  possible_duplicate: "Possible duplicate",
  overlap: "Overlaps a visit booked in Vaulted",
  changed_after_start: "Changed in Boulevard after it started here",
  cancelled_after_start: "Cancelled in Boulevard after it started here",
  gone: "No longer in Boulevard",
  no_chair: "Taken over from Boulevard without a chair: give it one",
  no_card: "No card on file (taken over from Boulevard, or an iPad walk-in who chose the front desk): collect one",
};

/** A visit as Boulevard or the imported record has it, for comparing side by side. */
export interface BoulevardVisitSummary {
  date: string;
  time: string | null;
  staff: string;
  services: string[];
  totalCents: number | null;
  status?: string;
}

export interface BoulevardFlagView {
  id: string;
  kind: BoulevardFlagKind;
  appointmentId: string;
  clientId: string;
  clientName: string;
  /** Clinic-local start of the visit. */
  start: string;
  createdAt: string;
  details: { boulevard?: BoulevardVisitSummary; other?: BoulevardVisitSummary; otherLabel?: string; otherAppointmentId?: string };
}
